import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useProceedings } from "../../hooks";
import type { Proceeding } from "../../types";
import { daysAgoLabel, fmtDate } from "../../utils";

/** Wybrana sprawa + (opcjonalnie) jej pozycja „wniosek" bez pakietu, do której pakiet ma
 *  się podpiąć zamiast zakładać nową pozycję przy wysyłce. `closed` — sprawa zamknięta
 *  (tylko z `allowClosed`): podpięcie ją otworzy, więc wymaga potwierdzenia `reopen`. */
export type CaseLink = { proceeding: string; item: string | null; closed?: boolean; reopen?: boolean };

/** Pozycje „wniosek (nasz)" sprawy, za którymi nie stoi żaden pakiet z modułu Wnioski. */
export const unlinkedApplicationItems = (p: Proceeding) =>
  p.items.filter((i) => i.kind === "application" && !i.official_application);

const normalizedRef = (v: string) => v.replace(/\s+/g, "").toLowerCase();
/** Znak pozycji wskazuje numer pakietu — ta sama reguła co wnioski.ref_matches_number:
 *  ten sam znak (bez spacji i wielkości liter) albo numer jako całość w dłuższym znaku. */
export const refMatchesNumber = (ref: string, number: string) => {
  const r = normalizedRef(ref);
  const n = normalizedRef(number);
  if (!r || !n) return false;
  const escaped = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return r === n || new RegExp(`(?<![0-9a-z])${escaped}(?![0-9a-z])`).test(r);
};

/**
 * Wybór OTWARTEJ sprawy z Korespondencji tego projektu (z `allowClosed` także zamkniętej —
 * pod listą otwartych; niezakończony pakiet otwiera ją przy podpięciu, więc pytamy o zgodę
 * na ponowne otwarcie samego statusu). Tryb „application": gdy sprawa ma
 * już pozycję „wniosek" bez pakietu, pyta wprost, czy podpiąć pakiet pod nią (wniosek
 * wysłany z Korespondencji też jest wnioskiem — pakiet dokłada mu tylko dokumenty) czy
 * dołożyć nową pozycję przy wysyłce. Tryb „decision": sam wybór sprawy.
 * Sprawy z adresatem pakietu idą na górę listy i mają gwiazdkę.
 */
export function CaseLinkPicker({
  connectionId,
  recipientOffice,
  mode = "application",
  allowClosed = false,
  packageNumber,
  value,
  onChange,
}: {
  connectionId: string;
  recipientOffice?: string | null;
  mode?: "application" | "decision";
  allowClosed?: boolean;
  /** Numer pakietu — pozycję o tym znaku zaznaczamy od razu (serwer i tak ją weźmie). */
  packageNumber?: string;
  value: CaseLink | null;
  onChange: (v: CaseLink | null) => void;
}) {
  const { data: cases, isLoading } = useProceedings(
    allowClosed ? { connection: connectionId } : { connection: connectionId, status: "open" },
  );

  const sorted = useMemo(() => {
    const list = [...(cases ?? [])];
    // otwarte przed zamkniętymi, w nich adresat pakietu na górę, dalej najnowsze (kolejność z API)
    const rank = (p: Proceeding) =>
      (p.status === "closed" ? 2 : 0) + (recipientOffice && p.recipient_office === recipientOffice ? 0 : 1);
    return list.sort((a, b) => rank(a) - rank(b));
  }, [cases, recipientOffice]);

  const options = useMemo(
    () => sorted.map((p) => ({
      value: p.id,
      label: [
        p.status === "closed" ? "zamknięta" : null,
        recipientOffice && p.recipient_office === recipientOffice ? "★" : null,
        `${p.type_name}${p.type_note ? ` — ${p.type_note}` : ""}`,
        p.office_name ?? "bez urzędu",
        `${p.parcel_ids.length} dz.`,
        `zał. ${fmtDate(p.created_at)}`,
      ].filter(Boolean).join(" · "),
    })),
    [sorted, recipientOffice],
  );

  const selected = value ? sorted.find((p) => p.id === value.proceeding) ?? null : null;
  const candidates = selected && mode === "application" ? unlinkedApplicationItems(selected) : [];
  const pickItem = (item: string | null) => selected && onChange({ ...value, proceeding: selected.id, item });
  const matchesPackage = (ref: string) => !!packageNumber && refMatchesNumber(ref, packageNumber);
  // Jedyna pozycja ze znakiem = numer pakietu to jego pismo — od razu zaznaczona.
  const matchedItem = (p: Proceeding) => {
    if (mode !== "application") return null;
    const hits = unlinkedApplicationItems(p).filter((it) => matchesPackage(it.our_ref));
    return hits.length === 1 ? hits[0].id : null;
  };

  if (!isLoading && (cases ?? []).length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Ten projekt nie ma {allowClosed ? "" : "otwartych "}spraw w Korespondencji — wysyłka założy nową.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <SearchableSelect
        options={options}
        value={value?.proceeding ?? ""}
        placeholder={isLoading ? "Ładuję sprawy…" : allowClosed ? "Wybierz sprawę…" : "Wybierz otwartą sprawę…"}
        onChange={(e) => {
          const picked = sorted.find((p) => p.id === e.target.value);
          if (!picked) onChange(null);
          else if (picked.status === "closed") onChange({ proceeding: picked.id, item: matchedItem(picked), closed: true, reopen: false });
          else onChange({ proceeding: picked.id, item: matchedItem(picked) });
        }}
      />
      {selected?.status === "closed" && value && (
        <label className="flex items-start gap-2 border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={!!value.reopen}
            onChange={(e) => onChange({ ...value, reopen: e.target.checked })}
          />
          <span>
            Sprawa jest zamknięta{selected.outcome_display ? ` (${selected.outcome_display.toLowerCase()})` : ""} —
            podpięcie <b>otworzy ją ponownie</b>. Cofamy tylko status: pozycje (np. odpowiedź urzędu),
            tytuły dostępu i inne pakiety zostają. Potwierdzam.
          </span>
        </label>
      )}
      {selected && selected.official_applications.length > 0 && (
        <p className="text-[11px] font-mono text-[var(--color-text-muted)]">
          Sprawa ma już pakiet: {selected.official_applications.map((a) =>
            `${a.number} (${a.status})`).join(", ")}
        </p>
      )}
      {selected && candidates.length > 0 && (
        <div
          className="border border-amber-300 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-500/10 p-2 space-y-1.5"
          role="group"
          aria-label="Istniejąca pozycja wniosku w sprawie"
        >
          <p className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300">
            <AlertTriangle size={13} className="mt-0.5 shrink-0" />
            Ta sprawa ma już {candidates.length === 1 ? "pozycję „Wniosek”" : `${candidates.length} pozycje „Wniosek”`} bez
            podpiętego pakietu. Podpiąć ten pakiet do istniejącej pozycji (pismo już poszło
            z Korespondencji, pakiet dołoży mu dokumenty), czy dołożyć nową przy wysyłce?
          </p>
          <div className="space-y-1">
            {candidates.map((it) => (
              <label key={it.id} className="flex items-start gap-2 text-xs font-mono cursor-pointer">
                <input
                  type="radio"
                  name={`case-item-${selected.id}`}
                  className="mt-0.5"
                  checked={value?.item === it.id}
                  onChange={() => pickItem(it.id)}
                />
                <span>
                  Podepnij do pozycji:{" "}
                  {it.our_ref ? <span className="text-[var(--color-text-primary)]">znak {it.our_ref}</span> : "bez znaku"}
                  {it.app_status_display && <> · {it.app_status_display}</>}
                  {it.date_sent && <> · wysłano {fmtDate(it.date_sent)} ({daysAgoLabel(it.date_sent)})</>}
                  {it.channel_display && <> · {it.channel_display}</>}
                  {matchesPackage(it.our_ref) && (
                    <span className="text-green-700 dark:text-green-400"> · znak = numer pakietu</span>
                  )}
                </span>
              </label>
            ))}
            <label className="flex items-start gap-2 text-xs font-mono cursor-pointer">
              <input
                type="radio"
                name={`case-item-${selected.id}`}
                className="mt-0.5"
                checked={value?.item === null}
                onChange={() => pickItem(null)}
              />
              <span>Nie — wysyłka doda nową pozycję „Wniosek” do tej sprawy</span>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
